import { useState, useEffect } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, Eye, EyeOff, ExternalLink, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { BilingualInput, BilingualTextarea } from "../../components/BilingualField";

export default function AdminCmsPages() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPage, setEditingPage] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`);
      setPages(res.data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm(t("admin_cms:delete_confirm", { defaultValue: "Delete this page? This cannot be undone." }))) return;
    try {
      await axios.delete(`${API}/admin/pages/${pageId}`);
      setPages(pages.filter((p) => p.page_id !== pageId));
      toast.success(t("admin_cms:deleted_toast", { defaultValue: "Page deleted" }));
    } catch (error) {
      toast.error(t("admin_cms:delete_failed_toast", { defaultValue: "Failed to delete page" }));
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      const res = await axios.put(`${API}/admin/pages/${page.page_id}`, {
        is_published: !page.is_published,
      });
      setPages(pages.map((p) => (p.page_id === page.page_id ? res.data : p)));
      toast.success(page.is_published
        ? t("admin_cms:unpublished_toast", { defaultValue: "Page unpublished" })
        : t("admin_cms:published_toast", { defaultValue: "Page published" }));
    } catch (error) {
      toast.error(t("admin_cms:update_failed_toast", { defaultValue: "Failed to update page" }));
    }
  };

  return (
    <div data-testid="admin-cms-pages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><FileText className="h-6 w-6 text-primary" />{t("admin_cms:title")}</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="cms-create-page-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("admin_cms:new_page", { defaultValue: "New Page" })}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("admin_cms:create_dialog_title", { defaultValue: "Create New Page" })}</DialogTitle>
            </DialogHeader>
            <PageForm
              onSuccess={(newPage) => {
                setPages([...pages, newPage]);
                setShowCreate(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      {editingPage && (
        <Dialog open={!!editingPage} onOpenChange={() => setEditingPage(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("admin_cms:edit_dialog_title", { defaultValue: "Edit Page" })}</DialogTitle>
            </DialogHeader>
            <PageForm
              page={editingPage}
              onSuccess={(updatedPage) => {
                setPages(pages.map((p) => (p.page_id === updatedPage.page_id ? updatedPage : p)));
                setEditingPage(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-muted h-16 rounded-lg" />
          ))}
        </div>
      ) : pages.length > 0 ? (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin_cms:col_title", { defaultValue: "Title" })}</TableHead>
                <TableHead>{t("admin_cms:col_slug", { defaultValue: "Slug" })}</TableHead>
                <TableHead>{t("admin_cms:col_status", { defaultValue: "Status" })}</TableHead>
                <TableHead>{t("admin_cms:col_updated", { defaultValue: "Updated" })}</TableHead>
                <TableHead className="text-right">{t("admin_cms:col_actions", { defaultValue: "Actions" })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.page_id} data-testid={`cms-page-row-${page.page_id}`}>
                  <TableCell className="font-medium">
                    {(lang === "el" && page.title_el) || page.title}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">/page/{page.slug}</TableCell>
                  <TableCell>
                    {page.is_published ? (
                      <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        {t("admin_cms:status_published", { defaultValue: "Published" })}
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200">
                        {t("admin_cms:status_draft", { defaultValue: "Draft" })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {page.updated_at ? format(new Date(page.updated_at), "MMM d, yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePublish(page)}
                        title={page.is_published ? t("admin_cms:unpublish", { defaultValue: "Unpublish" }) : t("admin_cms:publish", { defaultValue: "Publish" })}
                        data-testid={`cms-toggle-publish-${page.page_id}`}
                      >
                        {page.is_published ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingPage(page)}
                        data-testid={`cms-edit-${page.page_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {page.is_published && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`/page/${page.slug}`} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(page.page_id)}
                        data-testid={`cms-delete-${page.page_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <FileText className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("admin_cms:add_page")}</h3>
          <p className="text-muted-foreground mb-4">
            {t("admin_cms:empty_help", { defaultValue: "Create pages for About, Contact, Terms, Privacy, etc." })}
          </p>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("admin_cms:create_first", { defaultValue: "Create First Page" })}
          </Button>
        </div>
      )}
    </div>
  );
}

function PageForm({ page, onSuccess }) {
  const { t } = useTranslation();
  const isEdit = !!page;
  const [form, setForm] = useState({
    title: page?.title || "",
    title_el: page?.title_el || "",
    slug: page?.slug || "",
    content: page?.content || "",
    content_el: page?.content_el || "",
    meta_description: page?.meta_description || "",
    is_published: page?.is_published || false,
    sort_order: page?.sort_order || 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let res;
      if (isEdit) {
        res = await axios.put(`${API}/admin/pages/${page.page_id}`, form);
      } else {
        res = await axios.post(`${API}/admin/pages`, form);
      }
      toast.success(isEdit
        ? t("admin_cms:updated_toast", { defaultValue: "Page updated" })
        : t("admin_cms:created_toast", { defaultValue: "Page created" }));
      onSuccess(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <BilingualInput
          label={t("admin_cms:field_title", { defaultValue: "Title" }) + " *"}
          value={form.title}
          valueEl={form.title_el}
          onChange={(e) => setForm({
            ...form,
            title: e.target.value,
            ...(isEdit ? {} : { slug: generateSlug(e.target.value) }),
          })}
          onChangeEl={(e) => setForm({ ...form, title_el: e.target.value })}
          required
          data-testid="cms-page-title-input"
        />
        <div>
          <Label>{t("admin_cms:field_slug", { defaultValue: "Slug" })} *</Label>
          <Input
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            required
            className="mt-1"
            data-testid="cms-page-slug-input"
          />
        </div>
      </div>

      <BilingualTextarea
        label={t("admin_cms:field_content", { defaultValue: "Content (HTML)" })}
        value={form.content}
        valueEl={form.content_el}
        onChange={(e) => setForm({ ...form, content: e.target.value })}
        onChangeEl={(e) => setForm({ ...form, content_el: e.target.value })}
        placeholder="<h1>Page Title</h1><p>Your content here...</p>"
        rows={8}
        data-testid="cms-page-content-input"
      />

      <div>
        <Label>{t("admin_cms:field_meta", { defaultValue: "Meta Description" })}</Label>
        <Input
          value={form.meta_description}
          onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
          className="mt-1"
          placeholder={t("admin_cms:meta_placeholder", { defaultValue: "SEO description for this page" })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("admin_cms:col_status", { defaultValue: "Status" })}</Label>
          <Select
            value={form.is_published ? "published" : "draft"}
            onValueChange={(v) => setForm({ ...form, is_published: v === "published" })}
          >
            <SelectTrigger className="mt-1" data-testid="cms-page-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">{t("admin_cms:status_draft", { defaultValue: "Draft" })}</SelectItem>
              <SelectItem value="published">{t("admin_cms:status_published", { defaultValue: "Published" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_cms:field_sort_order", { defaultValue: "Sort Order" })}</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
      </div>

      <Button type="submit" disabled={loading} className="w-full" data-testid="cms-page-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        {isEdit ? t("admin_cms:update_btn", { defaultValue: "Update Page" }) : t("admin_cms:create_btn", { defaultValue: "Create Page" })}
      </Button>
    </form>
  );
}
